import { useEffect, useMemo } from "react";
import { useTagDataContext } from "@/contexts/TagDataContext.jsx";

/**
 * Custom hook for fetching tag data for an asset
 * Uses the TagDataContext to cache data across pages
 * @param {string|null} assetId - The asset ID to fetch data for
 * @param {boolean} enabled - Whether to fetch data (default: true)
 * @returns {{tagData: Object|null, isLoading: boolean, error: string|null}}
 */
export function useTagData(assetId, enabled = true) {
  const { getTagData, fetchTagData } = useTagDataContext();

  // Get cached data - memoize to avoid unnecessary re-renders
  // Always call hooks, even if disabled, to maintain hook order
  const cachedData = useMemo(() => {
    if (!enabled || !assetId) {
      return {
        tagData: null,
        isLoading: false,
        error: null,
      };
    }
    return getTagData(assetId);
  }, [getTagData, assetId, enabled]);

  const { tagData, isLoading, error } = cachedData;

  // Fetch data if not already cached or loading
  useEffect(() => {
    if (enabled && assetId) {
      // Only fetch if we don't have data and we're not already loading
      if (!tagData && !isLoading && !error) {
        fetchTagData(assetId);
      }
    }
  }, [assetId, enabled, tagData, isLoading, error, fetchTagData]);

  // Return null data if disabled (but hooks were still called)
  if (!enabled || !assetId) {
    return {
      tagData: null,
      isLoading: false,
      error: null,
    };
  }

  return { tagData, isLoading, error };
}
